/**
 * CreatePlanModal
 *
 * Modal for creating a new subscription plan with comprehensive configuration.
 * Used by superadmins in the Subscription Management page.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ClockIcon,
  ServerStackIcon,
  SparklesIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useSubscriptionStore, SubscriptionPlan } from '@/stores/subscriptionStore';
import { PLAN_TIERS, type PlanTier } from '../../utils/planTiers';

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (plan: SubscriptionPlan) => void;
  editingPlan?: SubscriptionPlan | null;
}

type BillingCycle = 'monthly' | 'quarterly' | 'annual';

interface FormState {
  // Basic Info
  planId: string;
  name: string;
  tier: PlanTier;
  description: string;
  isPublic: boolean;
  isActive: boolean;

  // Legacy included limits (org-level, kept for backward compat)
  encountersPerMonth: number;
  encountersUnlimited: boolean;
  transcriptionMinutes: number;
  transcriptionUnlimited: boolean;
  users: number;
  usersUnlimited: boolean;
  storageGB: number;
  storageUnlimited: boolean;

  // Per-clinical-user limits (new model)
  encountersPerClinicalUser: number;
  minutesPerClinicalUser: number;
  staffUsersIncluded: number;
  staffUnlimited: boolean;
  usePerUserModel: boolean;  // Toggle to switch between legacy and per-user model

  // Pricing - NEW per-user model (no base price)
  setupFeePerClinicalUser: number;    // One-time fee when clinical user is added
  monthlyPerClinicalUser: number;     // Monthly recurring per clinical user
  monthlyPerStaffUser: number;        // Monthly recurring per staff user
  currency: string;
  billingCycle: BillingCycle;

  // Legacy pricing (deprecated, kept for backward compat)
  basePrice: number;
  perUserPrice: number;
  perStaffUserPrice: number;

  // Overage settings
  allowOverages: boolean;
  encounterOveragePrice: number;
  minuteOveragePrice: number;
  maxOverageLimit: number;

  // Features
  enableClinicalCoding: boolean;
  enableCustomTemplates: boolean;
  enableAdvancedAnalytics: boolean;
  enableApiAccess: boolean;
  enablePrioritySupport: boolean;
  maxConcurrentRecordings: number;
}

const initialFormState: FormState = {
  planId: '',
  name: '',
  tier: 'professional',
  description: '',
  isPublic: true,
  isActive: true,

  // Legacy limits (org-level)
  encountersPerMonth: 100,
  encountersUnlimited: false,
  transcriptionMinutes: 500,
  transcriptionUnlimited: false,
  users: 10,
  usersUnlimited: false,
  storageGB: 50,
  storageUnlimited: false,

  // Per-clinical-user limits (new model - default enabled)
  encountersPerClinicalUser: 200,
  minutesPerClinicalUser: 500,
  staffUsersIncluded: 5,
  staffUnlimited: false,
  usePerUserModel: true,  // Default to new per-user model

  // NEW per-user pricing model
  setupFeePerClinicalUser: 99,     // One-time setup fee per clinical user
  monthlyPerClinicalUser: 49,      // Monthly fee per clinical user
  monthlyPerStaffUser: 10,         // Monthly fee per staff user
  currency: 'USD',
  billingCycle: 'monthly',

  // Legacy pricing (deprecated)
  basePrice: 0,
  perUserPrice: 0,
  perStaffUserPrice: 0,

  allowOverages: true,
  encounterOveragePrice: 5,
  minuteOveragePrice: 0.10,
  maxOverageLimit: 500,

  enableClinicalCoding: true,
  enableCustomTemplates: false,
  enableAdvancedAnalytics: false,
  enableApiAccess: false,
  enablePrioritySupport: false,
  maxConcurrentRecordings: 3
};

export const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingPlan
}) => {
  const { createPlan, updatePlan, plans } = useSubscriptionStore();
  const [form, setForm] = useState<FormState>(() => {
    if (editingPlan) {
      // Determine if plan uses per-user model
      const hasPerUserLimits = editingPlan.included.encountersPerClinicalUser !== undefined &&
                               editingPlan.included.encountersPerClinicalUser > 0;

      return {
        planId: editingPlan.planId,
        name: editingPlan.name,
        tier: editingPlan.tier,
        description: editingPlan.description || '',
        isPublic: editingPlan.isPublic,
        isActive: editingPlan.isActive,
        // Legacy limits
        encountersPerMonth: editingPlan.included.encountersPerMonth === -1 ? 0 : editingPlan.included.encountersPerMonth,
        encountersUnlimited: editingPlan.included.encountersPerMonth === -1,
        transcriptionMinutes: editingPlan.included.transcriptionMinutes === -1 ? 0 : editingPlan.included.transcriptionMinutes,
        transcriptionUnlimited: editingPlan.included.transcriptionMinutes === -1,
        users: editingPlan.included.users === -1 ? 0 : editingPlan.included.users,
        usersUnlimited: editingPlan.included.users === -1,
        storageGB: editingPlan.included.storageGB === -1 ? 0 : editingPlan.included.storageGB,
        storageUnlimited: editingPlan.included.storageGB === -1,
        // Per-clinical-user limits
        encountersPerClinicalUser: editingPlan.included.encountersPerClinicalUser || 200,
        minutesPerClinicalUser: editingPlan.included.minutesPerClinicalUser || 500,
        staffUsersIncluded: editingPlan.included.staffUsersIncluded || 0,
        staffUnlimited: false,
        usePerUserModel: hasPerUserLimits,
        // New per-user pricing
        setupFeePerClinicalUser: editingPlan.pricing.setupFeePerClinicalUser || 99,
        monthlyPerClinicalUser: editingPlan.pricing.monthlyPerClinicalUser || 49,
        monthlyPerStaffUser: editingPlan.pricing.monthlyPerStaffUser || 10,
        currency: editingPlan.pricing.currency,
        billingCycle: editingPlan.pricing.billingCycle,
        // Legacy pricing
        basePrice: editingPlan.pricing.basePrice || 0,
        perUserPrice: editingPlan.pricing.perUserPrice || 0,
        perStaffUserPrice: editingPlan.pricing.perStaffUserPrice || 0,
        // Overages
        allowOverages: editingPlan.defaultOverages.allowOverages,
        encounterOveragePrice: editingPlan.defaultOverages.encounterOveragePrice,
        minuteOveragePrice: editingPlan.defaultOverages.minuteOveragePrice,
        maxOverageLimit: editingPlan.defaultOverages.maxOverageLimit,
        // Features
        enableClinicalCoding: editingPlan.features.enableClinicalCoding,
        enableCustomTemplates: editingPlan.features.enableCustomTemplates,
        enableAdvancedAnalytics: editingPlan.features.enableAdvancedAnalytics,
        enableApiAccess: editingPlan.features.enableApiAccess,
        enablePrioritySupport: editingPlan.features.enablePrioritySupport,
        maxConcurrentRecordings: editingPlan.features.maxConcurrentRecordings
      };
    }
    return initialFormState;
  });
  const [activeTab, setActiveTab] = useState<'basics' | 'limits' | 'pricing' | 'features'>('basics');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editingPlan;

  // Update form when editingPlan changes (e.g., when clicking Edit on a different plan)
  useEffect(() => {
    if (editingPlan) {
      // Determine if plan uses per-user model
      const hasPerUserLimits = editingPlan.included.encountersPerClinicalUser !== undefined &&
                               editingPlan.included.encountersPerClinicalUser > 0;

      setForm({
        planId: editingPlan.planId,
        name: editingPlan.name,
        tier: editingPlan.tier,
        description: editingPlan.description || '',
        isPublic: editingPlan.isPublic,
        isActive: editingPlan.isActive,
        // Legacy limits
        encountersPerMonth: editingPlan.included.encountersPerMonth === -1 ? 0 : editingPlan.included.encountersPerMonth,
        encountersUnlimited: editingPlan.included.encountersPerMonth === -1,
        transcriptionMinutes: editingPlan.included.transcriptionMinutes === -1 ? 0 : editingPlan.included.transcriptionMinutes,
        transcriptionUnlimited: editingPlan.included.transcriptionMinutes === -1,
        users: editingPlan.included.users === -1 ? 0 : editingPlan.included.users,
        usersUnlimited: editingPlan.included.users === -1,
        storageGB: editingPlan.included.storageGB === -1 ? 0 : editingPlan.included.storageGB,
        storageUnlimited: editingPlan.included.storageGB === -1,
        // Per-clinical-user limits
        encountersPerClinicalUser: editingPlan.included.encountersPerClinicalUser || 200,
        minutesPerClinicalUser: editingPlan.included.minutesPerClinicalUser || 500,
        staffUsersIncluded: editingPlan.included.staffUsersIncluded || 0,
        staffUnlimited: false,
        usePerUserModel: hasPerUserLimits,
        // New per-user pricing
        setupFeePerClinicalUser: editingPlan.pricing.setupFeePerClinicalUser || 99,
        monthlyPerClinicalUser: editingPlan.pricing.monthlyPerClinicalUser || 49,
        monthlyPerStaffUser: editingPlan.pricing.monthlyPerStaffUser || 10,
        currency: editingPlan.pricing.currency,
        billingCycle: editingPlan.pricing.billingCycle,
        // Legacy pricing
        basePrice: editingPlan.pricing.basePrice || 0,
        perUserPrice: editingPlan.pricing.perUserPrice || 0,
        perStaffUserPrice: editingPlan.pricing.perStaffUserPrice || 0,
        // Overages
        allowOverages: editingPlan.defaultOverages.allowOverages,
        encounterOveragePrice: editingPlan.defaultOverages.encounterOveragePrice,
        minuteOveragePrice: editingPlan.defaultOverages.minuteOveragePrice,
        maxOverageLimit: editingPlan.defaultOverages.maxOverageLimit,
        // Features
        enableClinicalCoding: editingPlan.features.enableClinicalCoding,
        enableCustomTemplates: editingPlan.features.enableCustomTemplates,
        enableAdvancedAnalytics: editingPlan.features.enableAdvancedAnalytics,
        enableApiAccess: editingPlan.features.enableApiAccess,
        enablePrioritySupport: editingPlan.features.enablePrioritySupport,
        maxConcurrentRecordings: editingPlan.features.maxConcurrentRecordings
      });
      setActiveTab('basics');
    } else {
      // Reset to initial state when creating a new plan
      setForm(initialFormState);
      setActiveTab('basics');
    }
    setError(null);
  }, [editingPlan]);

  const handleChange = (field: keyof FormState, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const generatePlanId = () => {
    const slug = form.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    handleChange('planId', slug || 'plan-' + Date.now());
  };

  const handleSubmit = async () => {
    // === BASIC INFO VALIDATION ===
    if (!form.planId.trim()) {
      setError('Plan ID is required');
      setActiveTab('basics');
      return;
    }

    // Check for duplicate planId (only when creating)
    if (!isEditing) {
      const existingPlan = plans.find(p => p.planId === form.planId.trim());
      if (existingPlan) {
        setError(`A plan with ID "${form.planId}" already exists. Please choose a unique ID.`);
        setActiveTab('basics');
        return;
      }
    }

    if (!form.name.trim()) {
      setError('Plan name is required');
      setActiveTab('basics');
      return;
    }

    // === LIMITS VALIDATION ===
    if (form.usePerUserModel) {
      // Per-user model validations
      if (!form.encountersUnlimited && form.encountersPerClinicalUser <= 0) {
        setError('Encounters per clinical user must be greater than 0');
        setActiveTab('limits');
        return;
      }
      if (!form.transcriptionUnlimited && form.minutesPerClinicalUser <= 0) {
        setError('Minutes per clinical user must be greater than 0');
        setActiveTab('limits');
        return;
      }
    } else {
      // Legacy model validations
      if (!form.encountersUnlimited && form.encountersPerMonth <= 0) {
        setError('Encounters per month must be greater than 0');
        setActiveTab('limits');
        return;
      }
      if (!form.transcriptionUnlimited && form.transcriptionMinutes <= 0) {
        setError('Transcription minutes must be greater than 0');
        setActiveTab('limits');
        return;
      }
      if (!form.usersUnlimited && form.users <= 0) {
        setError('Users limit must be greater than 0');
        setActiveTab('limits');
        return;
      }
    }

    if (!form.storageUnlimited && form.storageGB <= 0) {
      setError('Storage must be greater than 0 GB');
      setActiveTab('limits');
      return;
    }

    // === PRICING VALIDATION ===
    // Check all pricing fields are non-negative
    const pricingFields = [
      { field: 'setupFeePerClinicalUser', label: 'Setup fee per clinical user' },
      { field: 'monthlyPerClinicalUser', label: 'Monthly fee per clinical user' },
      { field: 'monthlyPerStaffUser', label: 'Monthly fee per staff user' },
      { field: 'basePrice', label: 'Base price' },
      { field: 'perUserPrice', label: 'Per user price' },
      { field: 'perStaffUserPrice', label: 'Per staff user price' },
      { field: 'encounterOveragePrice', label: 'Encounter overage price' },
      { field: 'minuteOveragePrice', label: 'Minute overage price' },
      { field: 'maxOverageLimit', label: 'Max overage limit' }
    ];

    for (const { field, label } of pricingFields) {
      const value = form[field as keyof FormState] as number;
      if (value < 0) {
        setError(`${label} cannot be negative`);
        setActiveTab('pricing');
        return;
      }
    }

    // Validate at least one pricing model has values
    const hasPerUserPricing = form.monthlyPerClinicalUser > 0;
    const hasLegacyPricing = form.basePrice > 0 || form.perUserPrice > 0;

    if (!hasPerUserPricing && !hasLegacyPricing) {
      setError('Please set a monthly fee per clinical user, or configure legacy pricing');
      setActiveTab('pricing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const planData: Partial<SubscriptionPlan> = {
        planId: form.planId,
        name: form.name,
        tier: form.tier,
        description: form.description || undefined,
        isPublic: form.isPublic,
        isActive: form.isActive,
        included: {
          // Legacy limits (still stored for backward compat)
          encountersPerMonth: form.encountersUnlimited ? -1 : form.encountersPerMonth,
          transcriptionMinutes: form.transcriptionUnlimited ? -1 : form.transcriptionMinutes,
          users: form.usersUnlimited ? -1 : form.users,
          storageGB: form.storageUnlimited ? -1 : form.storageGB,
          // Per-clinical-user limits (new model)
          ...(form.usePerUserModel && {
            encountersPerClinicalUser: form.encountersPerClinicalUser,
            minutesPerClinicalUser: form.minutesPerClinicalUser,
            staffUsersIncluded: form.staffUnlimited ? -1 : form.staffUsersIncluded
          })
        },
        pricing: {
          // New per-user pricing model
          setupFeePerClinicalUser: form.setupFeePerClinicalUser,
          monthlyPerClinicalUser: form.monthlyPerClinicalUser,
          monthlyPerStaffUser: form.monthlyPerStaffUser,
          currency: form.currency,
          billingCycle: form.billingCycle,
          // Legacy (for backward compat)
          basePrice: form.basePrice || undefined,
          perUserPrice: form.perUserPrice || undefined,
          perStaffUserPrice: form.perStaffUserPrice || undefined
        },
        defaultOverages: {
          allowOverages: form.allowOverages,
          encounterOveragePrice: form.encounterOveragePrice,
          minuteOveragePrice: form.minuteOveragePrice,
          maxOverageLimit: form.maxOverageLimit
        },
        features: {
          enableClinicalCoding: form.enableClinicalCoding,
          enableCustomTemplates: form.enableCustomTemplates,
          enableAdvancedAnalytics: form.enableAdvancedAnalytics,
          enableApiAccess: form.enableApiAccess,
          enablePrioritySupport: form.enablePrioritySupport,
          maxConcurrentRecordings: form.maxConcurrentRecordings
        }
      };

      let result: SubscriptionPlan;
      if (isEditing) {
        result = await updatePlan(editingPlan!.planId, planData);
      } else {
        result = await createPlan(planData);
      }

      onSuccess?.(result);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basics' as const, label: 'Basic Info', icon: DocumentTextIcon },
    { id: 'limits' as const, label: 'Limits', icon: ServerStackIcon },
    { id: 'pricing' as const, label: 'Pricing', icon: CurrencyDollarIcon },
    { id: 'features' as const, label: 'Features', icon: SparklesIcon }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-purple-50 border-b border-purple-100">
              <h2 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                <DocumentTextIcon className="h-6 w-6" />
                {isEditing ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
              </h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-purple-600" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Basics Tab */}
              {activeTab === 'basics' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => !form.planId && generatePlanId()}
                      placeholder="Professional Plan"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan ID *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={form.planId}
                        onChange={(e) => handleChange('planId', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        placeholder="professional-plan"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={isEditing}
                      />
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={generatePlanId}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                        >
                          Generate
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Unique identifier for this plan. Cannot be changed after creation.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier
                    </label>
                    <select
                      value={form.tier}
                      onChange={(e) => handleChange('tier', e.target.value as PlanTier)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {PLAN_TIERS.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Best for growing practices..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isPublic}
                        onChange={(e) => handleChange('isPublic', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Public plan</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Limits Tab */}
              {activeTab === 'limits' && (
                <div className="space-y-6">
                  {/* Per-User Model Toggle */}
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div>
                      <h4 className="text-sm font-medium text-purple-800">Per-Clinical-User Model</h4>
                      <p className="text-xs text-purple-600 mt-1">
                        Limits are multiplied by the number of clinical users (operators, admins with recording access)
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.usePerUserModel}
                        onChange={(e) => handleChange('usePerUserModel', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {form.usePerUserModel ? (
                    <>
                      {/* Per-Clinical-User Limits */}
                      <div className="bg-purple-50/50 p-4 rounded-lg border border-purple-100">
                        <h4 className="text-sm font-medium text-purple-800 mb-4 flex items-center gap-2">
                          <UsersIcon className="h-5 w-5" />
                          Per Clinical User Limits
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-center gap-4 p-3 bg-white rounded-lg">
                            <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">Encounters per Clinical User</label>
                              <input
                                type="number"
                                value={form.encountersPerClinicalUser}
                                onChange={(e) => handleChange('encountersPerClinicalUser', parseInt(e.target.value) || 0)}
                                min={0}
                                className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 p-3 bg-white rounded-lg">
                            <ClockIcon className="h-5 w-5 text-gray-400" />
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700">Minutes per Clinical User</label>
                              <input
                                type="number"
                                value={form.minutesPerClinicalUser}
                                onChange={(e) => handleChange('minutesPerClinicalUser', parseInt(e.target.value) || 0)}
                                min={0}
                                className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Staff Users Section */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-800 mb-2 flex items-center gap-2">
                          <UsersIcon className="h-5 w-5" />
                          Staff Users (Read-Only)
                        </h4>
                        <p className="text-xs text-gray-600 mb-4">
                          Staff users can view but not create recordings. Lower cost tier.
                        </p>
                        <LimitField
                          icon={<UsersIcon className="h-5 w-5" />}
                          label="Staff Users Included"
                          value={form.staffUsersIncluded}
                          onChange={(v) => handleChange('staffUsersIncluded', v)}
                          unlimited={form.staffUnlimited}
                          onUnlimitedChange={(v) => handleChange('staffUnlimited', v)}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Legacy Org-Level Limits */}
                      <LimitField
                        icon={<DocumentTextIcon className="h-5 w-5" />}
                        label="Encounters per Month (Organization Total)"
                        value={form.encountersPerMonth}
                        onChange={(v) => handleChange('encountersPerMonth', v)}
                        unlimited={form.encountersUnlimited}
                        onUnlimitedChange={(v) => handleChange('encountersUnlimited', v)}
                      />

                      <LimitField
                        icon={<ClockIcon className="h-5 w-5" />}
                        label="Transcription Minutes (Organization Total)"
                        value={form.transcriptionMinutes}
                        onChange={(v) => handleChange('transcriptionMinutes', v)}
                        unlimited={form.transcriptionUnlimited}
                        onUnlimitedChange={(v) => handleChange('transcriptionUnlimited', v)}
                      />

                      <LimitField
                        icon={<UsersIcon className="h-5 w-5" />}
                        label="Users"
                        value={form.users}
                        onChange={(v) => handleChange('users', v)}
                        unlimited={form.usersUnlimited}
                        onUnlimitedChange={(v) => handleChange('usersUnlimited', v)}
                      />
                    </>
                  )}

                  {/* Storage always shown */}
                  <LimitField
                    icon={<ServerStackIcon className="h-5 w-5" />}
                    label="Storage (GB)"
                    value={form.storageGB}
                    onChange={(v) => handleChange('storageGB', v)}
                    unlimited={form.storageUnlimited}
                    onUnlimitedChange={(v) => handleChange('storageUnlimited', v)}
                  />
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-6">
                  {/* One-Time Setup Fees Section */}
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <h4 className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                      <CurrencyDollarIcon className="h-5 w-5" />
                      One-Time Setup Fees
                    </h4>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Setup Fee per Clinical User
                      </label>
                      <div className="relative w-48">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={form.setupFeePerClinicalUser}
                          onChange={(e) => handleChange('setupFeePerClinicalUser', parseFloat(e.target.value) || 0)}
                          min={0}
                          step={0.01}
                          placeholder="e.g., 99"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Charged once when a clinical user is added to the organization
                      </p>
                    </div>
                  </div>

                  {/* Monthly Recurring Section */}
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="text-sm font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <ClockIcon className="h-5 w-5" />
                      Monthly Recurring (Per User)
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Per Clinical User
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={form.monthlyPerClinicalUser}
                            onChange={(e) => handleChange('monthlyPerClinicalUser', parseFloat(e.target.value) || 0)}
                            min={0}
                            step={0.01}
                            placeholder="e.g., 49"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Monthly fee per clinical user
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Per Staff User
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={form.monthlyPerStaffUser}
                            onChange={(e) => handleChange('monthlyPerStaffUser', parseFloat(e.target.value) || 0)}
                            min={0}
                            step={0.01}
                            placeholder="e.g., 10"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Monthly fee per staff user (read-only access)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Billing Cycle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Cycle
                    </label>
                    <select
                      value={form.billingCycle}
                      onChange={(e) => handleChange('billingCycle', e.target.value as BillingCycle)}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <label className="flex items-center gap-2 cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={form.allowOverages}
                        onChange={(e) => handleChange('allowOverages', e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Allow overages</span>
                    </label>

                    {form.allowOverages && (
                      <div className="space-y-4 pl-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Per encounter overage
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={form.encounterOveragePrice}
                                onChange={(e) => handleChange('encounterOveragePrice', parseFloat(e.target.value) || 0)}
                                min={0}
                                step={0.01}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Per minute overage
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                value={form.minuteOveragePrice}
                                onChange={(e) => handleChange('minuteOveragePrice', parseFloat(e.target.value) || 0)}
                                min={0}
                                step={0.01}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Maximum overage limit per period
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={form.maxOverageLimit}
                              onChange={(e) => handleChange('maxOverageLimit', parseFloat(e.target.value) || 0)}
                              min={0}
                              step={1}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Usage will be blocked when overage reaches this amount.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <div className="space-y-4">
                  <FeatureToggle
                    label="ICD-10/CPT Clinical Coding"
                    description="Enable AI-powered diagnostic and procedure code suggestions"
                    checked={form.enableClinicalCoding}
                    onChange={(v) => handleChange('enableClinicalCoding', v)}
                  />

                  <FeatureToggle
                    label="Custom Note Templates"
                    description="Allow users to create and use custom note templates"
                    checked={form.enableCustomTemplates}
                    onChange={(v) => handleChange('enableCustomTemplates', v)}
                  />

                  <FeatureToggle
                    label="Advanced Analytics"
                    description="Access to detailed usage analytics and reporting"
                    checked={form.enableAdvancedAnalytics}
                    onChange={(v) => handleChange('enableAdvancedAnalytics', v)}
                  />

                  <FeatureToggle
                    label="API Access"
                    description="Enable REST API access for integrations"
                    checked={form.enableApiAccess}
                    onChange={(v) => handleChange('enableApiAccess', v)}
                  />

                  <FeatureToggle
                    label="Priority Support"
                    description="24/7 priority support with dedicated account manager"
                    checked={form.enablePrioritySupport}
                    onChange={(v) => handleChange('enablePrioritySupport', v)}
                  />

                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Concurrent Recordings
                    </label>
                    <input
                      type="number"
                      value={form.maxConcurrentRecordings}
                      onChange={(e) => handleChange('maxConcurrentRecordings', parseInt(e.target.value) || 1)}
                      min={1}
                      max={100}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum number of simultaneous active recordings for this organization.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <InformationCircleIcon className="h-4 w-4" />
                <span>Changes take effect immediately for new subscriptions</span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      {isEditing ? 'Update Plan' : 'Create Plan'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Helper component for limit fields
const LimitField: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  onChange: (value: number) => void;
  unlimited: boolean;
  onUnlimitedChange: (value: boolean) => void;
}> = ({ icon, label, value, onChange, unlimited, onUnlimitedChange }) => (
  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
    <div className="text-gray-400">{icon}</div>
    <div className="flex-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-4">
        <input
          type="number"
          value={unlimited ? '' : value}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          disabled={unlimited}
          min={0}
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => onUnlimitedChange(e.target.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm text-gray-600">Unlimited</span>
        </label>
      </div>
    </div>
  </div>
);

// Helper component for feature toggles
const FeatureToggle: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
    />
    <div>
      <p className="font-medium text-gray-900">{label}</p>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  </label>
);

export default CreatePlanModal;
